import React, { createContext, useContext, useEffect, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import DragIndicatorIcon from '../../assets/icons/drag-indicator.svg';
import { ReactComponent as ShuffleIcon } from '../../assets/icons/shuffle.svg';

import {
  CheckboxField,
  DateInput,
  EmailInput,
  FileUploadField,
  FormulaField,
  HeaderInput,
  ImageField,
  PaymentField,
  PhoneInput,
  RadioField,
  RatingInput,
  SectionDivider,
  SelectField,
  TextInput,
  VideoField,
  NumberInput,
  AddressField,
} from './FormFields';

import { Copy, Trash2 } from 'react-feather';
import {
  Button,
  ConfigProvider,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  BranchesOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  GroupOutlined,
  MergeCellsOutlined,
  PartitionOutlined,
  PullRequestOutlined,
  VerticalAlignTopOutlined,
} from '@ant-design/icons';
import Form from 'antd/lib/form/Form';

import './style.less';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { locales } from '../../utils/antdLocale';
import Title from 'antd/lib/typography/Title';
import { ADVANCED_QUESTION_TYPES } from '../../../../server/src/common/utils/constants';
import { values } from 'lodash-es';
import { hasFeature } from '../../utils/restrictions';
import { showUpselling } from '../../store/actions/paymentActions';

/**
 * @typedef {Object} StagingAreaContextType
 * @property {Object[]} questions - Array of question objects
 * @property {Object} sectionMap - Map of sections
 * @property {string[]} sectionOrder - Order of sections
 * @property {string} selectedQuestionId - ID of the selected question
 * @property {function(string): void} selectQuestion - Function to select a question
 * @property {function(string): void} deleteElement - Function to delete an element
 * @property {function(string): void} cloneElement - Function to clone an element
 * @property {function(string): void} openLogics - Function to open logics
 * @property {string} formLanguage - Current form language
 * @property {boolean} planHasAdvancedFields - Whether the plan has advanced fields
 * @property {function} t - Translation function
 * @property {boolean} isMobile - Whether the device is mobile
 */

/**
 * @type {React.Context<StagingAreaContextType>}
 */
const StagingAreaContext = createContext();

export const useStagingAreaContext = () => {
  const context = useContext(StagingAreaContext);
  if (!context) {
    throw new Error('useStagingArea must be used within a StagingAreaProvider');
  }
  return context;
};

// New component for generating form fields
const FormField = ({ item, errors }) => {
  const { t, formLanguage, planHasAdvancedFields } = useStagingAreaContext();
  switch (item.type) {
    case 'HEADER':
      return <HeaderInput item={item} />;
    case 'TEXT':
      return <TextInput item={item} />;
    case 'NUMBER':
      return <NumberInput item={item} />;
    case 'RATING':
      return <RatingInput item={item} />;
    case 'DROPDOWN':
      return <SelectField item={item} />;
    case 'RADIO':
      return <RadioField item={item} errors={errors} t={t} />;
    case 'CHECKBOX':
      return <CheckboxField item={item} errors={errors} t={t} />;
    case 'DATE':
      return <DateInput item={item} />;
    case 'IMAGE':
      return <ImageField item={item} />;
    case 'VIDEO':
      return <VideoField item={item} />;
    case 'EMAIL':
      return <EmailInput item={item} />;
    case 'PHONE':
      return <PhoneInput item={item} />;
    case 'ADDRESS':
      return <AddressField item={item} />;
    case 'FILE':
      return (
        <FileUploadField
          item={item}
          formLanguage={formLanguage}
          planHasAdvancedFields={planHasAdvancedFields}
        />
      );
    case 'FORMULA':
      return <FormulaField item={item} planHasAdvancedFields={planHasAdvancedFields} />;
    case 'PAYMENT':
      return <PaymentField item={item} planHasAdvancedFields={planHasAdvancedFields} />;
    case 'SECTION':
      return <SectionDivider item={item} planHasAdvancedFields={planHasAdvancedFields} />;
    default:
      return <TextInput item={item} />;
  }
};

// New component for action buttons
const ActionButtons = ({ item, index }) => {
  const { selectedQuestionId, questions, cloneElement, deleteElement, openLogics, t, isMobile } =
    useStagingAreaContext();
  const [openDeleteQuestionPopconfirm, setOpenDeleteQuestionPopconfirm] = useState(false);
  const dispatch = useDispatch();

  const { me } = useSelector((state) => state.auth);

  const toggleActionButtonVisiblity = (index, itemId, visible) => {
    const formElContainer = document.querySelector(`[data-element-container-id="${itemId}"]`);

    if (!formElContainer) return;
    if (visible) {
      // add hovering class to cotnainer to prevent button for hidding when hovered on the popconfirm
      formElContainer.classList.add('hovering');
    } else {
      formElContainer.classList.remove('hovering');
    }
  };

  const isSection = item?.type === 'SECTION';

  return (
    <div
      className={classNames(`action-buttons`, {
        show: isMobile && item?.questionId === selectedQuestionId,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        icon={<PullRequestOutlined />}
        onClick={() => {
          if (!hasFeature(me.plan, 'conditionalLogic')) {
            return dispatch(showUpselling('conditionalLogic'));
          }
          openLogics(item.questionId);
        }}
        size="small"
        title="Open Logic"
      />
      <Button
        icon={<CopyOutlined />}
        onClick={() => cloneElement(item.questionId)}
        size="small"
        title="copy"
      />
      <Popconfirm
        visible={openDeleteQuestionPopconfirm === item.questionId}
        onCancel={() => setOpenDeleteQuestionPopconfirm(null)}
        title={
          <div style={{ maxWidth: '400px' }}>
            <Title level={5} style={{ fontSize: '15px' }}>
              {t(
                isSection
                  ? 'builder:stagingArea.delete.sectionLabel'
                  : 'builder:stagingArea.delete.label',
              )}
            </Title>
            <Typography.Paragraph
              style={{
                fontSize: '14px',
                color: 'dimgrey',
                marginBottom: '0.5em',
              }}
            >
              {t(
                isSection
                  ? 'builder:stagingArea.delete.sectionDescription'
                  : 'builder:stagingArea.delete.description',
              )}
            </Typography.Paragraph>
          </div>
        }
        onConfirm={() => deleteElement(item.questionId)}
        onVisibleChange={(visible) => {
          if (!visible) setOpenDeleteQuestionPopconfirm(null);
          toggleActionButtonVisiblity(index, item.questionId, visible);
        }}
        okText={t('translation:common.yes', 'Yes')}
        cancelText={t('translation:common.no', 'No')}
        placement="bottomRight"
        arrowPointAtCenter
      >
        <Button
          icon={<DeleteOutlined />}
          type="primary"
          danger
          size="small"
          title="delete"
          onClick={() => {
            // check if this question is used in expression based questions
            // if yes, show a warning that this question is used in other questions

            //TODO: Make it DRY
            const questionsWithExpressions = questions.filter(
              (q) => q.type === 'FORMULA' || q.type === 'PAYMENT',
            );

            const hasVariableDependency = questionsWithExpressions.some((q) => {
              switch (q.type) {
                case 'FORMULA': {
                  const fields = values(q.formula?.variables);
                  return fields.includes(item.questionId);
                }
                case 'PAYMENT': {
                  return (
                    q?.paymentAmountType === 'linkedField' && q?.linkedField === item.questionId
                  );
                }
                default:
                  return false;
              }
            });

            if (!hasVariableDependency) return setOpenDeleteQuestionPopconfirm(item.questionId);

            Modal.error({
              title: 'Cannot delete this question',
              content: (
                <>
                  <Typography.Paragraph>
                    This question is used in the following fields:
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    <ul>
                      {questionsWithExpressions.map((q) => {
                        const fields = values(q.formula?.variables);
                        if (fields.includes(item.questionId)) {
                          return <li key={q.questionId}>{q.label}</li>;
                        }
                        if (
                          q?.paymentAmountType === 'linkedField' &&
                          q?.linkedField === item.questionId
                        ) {
                          return <li key={q.questionId}>{q.label}</li>;
                        }
                      })}
                    </ul>
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    Please remove from the above fields before deleting it.
                  </Typography.Paragraph>
                </>
              ),
              icon: <ExclamationCircleFilled />,
              closable: true,
              centered: true,
              width: 500,
              className: 'delete-depended-question-confirm',
            });

            setTimeout(() => {
              toggleActionButtonVisiblity(index, item.questionId, false);
            });
          }}
        />
      </Popconfirm>
    </div>
  );
};

// New component for questions within a section
const SectionQuestions = ({ sectionKey, sectionQuestions }) => {
  const {
    selectedQuestionId,
    selectQuestion,
    planHasAdvancedFields,
    formLanguage,
    sectionMap,
    sectionOrder,
    cloneElement,
    deleteElement,
    openLogics,
    t,
    questions,
  } = useStagingAreaContext();

  const [isHovering, setIsHovering] = useState(null);
  const errors = useSelector((state) => state.builderErrors);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  return (
    <Droppable droppableId={sectionKey} type="QUESTION">
      {(dropProvided, dropSnapshot) => (
        <div {...dropProvided.droppableProps}>
          <div
            ref={dropProvided.innerRef}
            style={{
              background: '#ffffff',
              padding: '24px 0',
              borderRadius: '5px',
              border: '2px solid #f0f0f3',
            }}
          >
            {sectionQuestions?.length ? (
              <>
                {sectionQuestions.map((item, index) => {
                  const selected = item.questionId === selectedQuestionId;
                  return (
                    <Draggable
                      key={item.questionId}
                      draggableId={item.questionId}
                      index={index}
                      // isDragDisabled={!selected}
                      disableInteractiveElementBlocking
                    >
                      {(provided, snapshot) => {
                        return (
                          <div
                            className={classNames(`form-element-container ${item.questionId}`, {
                              selected,
                              dragging: snapshot.isDragging,
                              error:
                                ADVANCED_QUESTION_TYPES.includes(item?.type) &&
                                !planHasAdvancedFields,
                            })}
                            data-element-container-id={item.questionId}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              zIndex: snapshot.isDragging && 2,
                            }}
                            onClick={() => selectQuestion(item?.questionId)}
                            onMouseEnter={() => {
                              setIsHovering(item?.questionId);
                            }}
                            onMouseLeave={() => setIsHovering(null)}
                          >
                            <div
                              className="drag-handle"
                              style={{
                                visibility:
                                  selected || isHovering === item?.questionId
                                    ? 'visible'
                                    : 'hidden',
                              }}
                            >
                              <img src={DragIndicatorIcon} alt="drag icon" />
                            </div>

                            <ActionButtons item={item} index={index} />

                            <FormField
                              item={item}
                              errors={errors}
                              t={t}
                              formLanguage={formLanguage}
                              planHasAdvancedFields={planHasAdvancedFields}
                            />
                            {item?.type !== 'RADIO' && item?.rules?.length > 0 && (
                              <Space
                                size={0}
                                style={{
                                  // gap: '0px',
                                  paddingInline: '16px',
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  paddingBottom: '16px',
                                }}
                              >
                                {item?.rules?.map((rule, i) => {
                                  if (!rule?.jumpToSection) return null;
                                  return (
                                    <div key={rule.jumpToSection}>
                                      {/* <Typography.Text
                                        style={{ fontSize: 14, cursor: 'pointer' }}
                                        type="secondary"
                                        onClick={(e) => {
                                          // scroll to the section
                                          e.stopPropagation();
                                          const section = document.querySelector(
                                            `[data-element-container-id="${rule.jumpToSection}"]`,
                                          );
                                          section.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                      >
                                        <PartitionOutlined style={{ marginRight: 4 }} />
                                        {sectionMap[rule.jumpToSection]?.label ||
                                          `Page ${sectionOrder.indexOf(rule.jumpToSection) + 1}`}
                                      </Typography.Text> */}
                                      <Tag
                                        icon={
                                          <span className="anticon" role="img">
                                            <ShuffleIcon />
                                          </span>
                                        }
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                          // scroll to the section
                                          e.stopPropagation();
                                          const section = document.querySelector(
                                            `[data-element-container-id="${rule.jumpToSection}"]`,
                                          );
                                          section.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                      >
                                        {rule.jumpToSection === 'END'
                                          ? 'Submit Form'
                                          : sectionMap[rule.jumpToSection]?.label ||
                                            `Page ${sectionOrder.indexOf(rule.jumpToSection) + 1}`}
                                      </Tag>
                                    </div>
                                  );
                                })}
                              </Space>
                            )}
                          </div>
                        );
                      }}
                    </Draggable>
                  );
                })}
                {dropProvided.placeholder}
              </>
            ) : (
              <div
                className={classNames('dropzone-area', {
                  dragging: dropSnapshot.isDraggingOver,
                })}
                style={{ marginInline: '24px' }}
              >
                {t('builder:stagingArea.placeholder', 'Drag and drop from sidebar')}
              </div>
            )}
          </div>
        </div>
      )}
    </Droppable>
  );
};

// New component for section container
const SectionContainer = ({ section, index, provided, snapshot }) => {
  const { selectedQuestionId, selectQuestion, sectionOrder, sectionMap, planHasAdvancedFields } =
    useStagingAreaContext();

  const [isHovering, setIsHovering] = useState(null);
  const selected = section?.questionId === selectedQuestionId;
  return (
    <div ref={provided.innerRef} {...provided.draggableProps} className="section-container">
      {sectionOrder?.length > 1 && (
        <div
          className={classNames(`section-element-container ${section?.questionId}`)}
          data-element-container-id={section?.questionId}
          {...provided.dragHandleProps}
          onClick={() => selectQuestion(section?.questionId)}
          onMouseEnter={() => setIsHovering(section?.questionId)}
          onMouseLeave={() => setIsHovering(null)}
        >
          <SectionDivider
            item={section}
            selected={!snapshot.isDragging && section?.questionId === selectedQuestionId}
            planHasAdvancedFields={planHasAdvancedFields}
          />
          <div
            className="drag-handle"
            style={{
              visibility: selected || isHovering === section?.questionId ? 'visible' : 'hidden',
            }}
          >
            <img src={DragIndicatorIcon} alt="drag icon" />
          </div>
          <ActionButtons item={section} index={index} />
        </div>
      )}
      <SectionQuestions sectionKey={section.questionId} sectionQuestions={section.questions} />
      {section.jumpToSection && (
        <div
          style={{
            padding: '8px 2px 24px 8px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Typography.Text style={{ fontSize: 14 }} type="secondary">
            Next page
            {/* <Typography.Text style={{ fontSize: 14 }} type="secondary">
              <BranchesOutlined style={{ marginRight: 6, marginLeft: 6 }} />
              {sectionMap[section.jumpToSection]?.label ||
                `Page ${sectionOrder.indexOf(section.jumpToSection) + 1}`}
            </Typography.Text> */}
            <Tag
              icon={
                <span className="anticon" role="img">
                  <ShuffleIcon />
                </span>
              }
              style={{ marginLeft: 8, marginRight: 0, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                const sectionEl = document.querySelector(
                  `[data-element-container-id="${section?.jumpToSection}"]`,
                );
                sectionEl?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {section.jumpToSection === 'END'
                ? 'Submit Form'
                : sectionMap[section.jumpToSection]?.label ||
                  `Page ${sectionOrder.indexOf(section.jumpToSection) + 1}`}
            </Tag>
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

const StagingAreaWithSections = ({
  questions,
  sectionMap,
  sectionOrder,
  selectedQuestionId,
  selectQuestion,
  deleteElement,
  cloneElement,
  openLogics,
  openProperites,
  formLanguage,
  planHasAdvancedFields,
}) => {
  const [locale, setLocale] = useState(null);
  const [isHovering, setIsHovering] = useState(null);

  useEffect(() => {
    if (!formLanguage) return;
    if (formLanguage === 'en') return setLocale(null);
    setLocale(locales[formLanguage]);
  }, [formLanguage]);

  const { t } = useTranslation(['builder', 'translation']);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  /**
   * @type {StagingAreaContextType}
   */
  const contextValue = {
    questions,
    sectionMap,
    sectionOrder,
    selectedQuestionId,
    selectQuestion,
    deleteElement,
    cloneElement,
    openLogics,
    formLanguage,
    planHasAdvancedFields,
    t,
    isMobile,
  };

  return (
    <StagingAreaContext.Provider value={contextValue}>
      <ConfigProvider locale={locale}>
        <Droppable droppableId="PREVIEW" direction="vertical" type="SECTION">
          {(provided, snapshot) => (
            <Form layout="vertical">
              <div
                ref={provided.innerRef}
                className={classNames('preview-dropzone', {
                  dragging: snapshot.isDraggingOver,
                })}
                {...provided.droppableProps}
              >
                {sectionOrder.map((key, index) => {
                  const section = sectionMap[key];
                  return (
                    <Draggable
                      draggableId={key}
                      index={index}
                      key={key}
                      isDragDisabled={sectionOrder?.length <= 1}
                    >
                      {(provided, snapshot) => (
                        <SectionContainer
                          section={section}
                          index={index}
                          provided={provided}
                          snapshot={snapshot}
                        />
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            </Form>
          )}
        </Droppable>
      </ConfigProvider>
    </StagingAreaContext.Provider>
  );
};

export default StagingAreaWithSections;
